// React Hooks
import { createContext, useContext, useState, useEffect } from "react";
// Global Hooks
import { useAuth } from "./AuthContext";
import { API_BASE_URL } from "../config";
// External Hooks/Components
import { useAuth as useClerkAuth } from "@clerk/react";


// Create React Context
const DataContext = createContext();


// ============
// DataProvider – provides GC related to general Product data.
// ============
export default function DataProvider({ children }){

    // Destructure GC
    const { activeUserJson, isSubscribed, checkIsSubscribed } = useAuth();
    const { isLoading: isClerkLoading, isSignedIn, getToken } = useClerkAuth(); // @Clerk

    // ==========
    // Sleep(int) – helper function that provides a basic async sleep delay
    // ==========
    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    
    // ================
    // ACTIVE ENGINE ID (String) – the Postgres UUID of the currently active Skybase Engine.
    // NOTE: ⚠️ Internal (DataContext.js) usage only.
    // ================
    // state var
    const [activeEngineId, setActiveEngineId] = useState(() => {
        
        // retrieve localStorage, otherwise 'null'
        const saved = localStorage.getItem("activeEngineId");
        return saved ? JSON.parse(saved) : null;
    });
    // localStorage caching
    useEffect(() => {

        // cache all !undefined activeEngineId
        if (activeEngineId !== null) {
            localStorage.setItem("activeEngineId", JSON.stringify(activeEngineId));
        }

    }, [activeEngineId]);
    // onMount – gets the UUID of the default Engine
    // useEffect(() => {

    //     // Edge case: Don't load the defaultEngineId if activeEngineId !null
    //     if (activeEngineId !== null) return;

    //     // Async fetch() function
    //     const loadDefaultEngine = async () => {
    //         try {
                
    //             // Edge case: Never fetch() if <Clerk JWT> DNE.
    //             const token = await getToken();
    //             if (!token) { return; };
                
    //             // `GET /engines/default`
    //             const response = await fetch(`${API_BASE_URL}/api/v1/engines/default`,
    //                 {
    //                     headers: {
    //                         Authorization: `Bearer ${token}`,
    //                     },
    //                 }
    //             );
                
    //             // Error handle
    //             if (!response.ok) { throw new Error(`SKYBASE: Unable to fetch Engine ID: ${activeEngineId}`) }
                
    //             // extract JSON
    //             const default_engine_json = await response.json();

    //             // update JSON (triggers re-render)
    //             setActiveEngineId(default_engine_json.id);

    //         } catch (err) {
    //             console.error("Failed to load default engine:", err);
    //         }
    //     };

    //     // call async function
    //     loadDefaultEngine();

    // }, []);
    // changeActiveEngineId(String) – changes the activeEngineId
    function changeActiveEngineId(engineId) {

        // update state var
        setActiveEngineId(engineId);

        // ⚠️ developer debugging
        // console.log(`SKYBASE (client): Changed to new active Engine with ID: ${engineId}`);
    }
    
    
    // ==================
    // ACTIVE ENGINE JSON
    // ==================
    // state var
    const [activeEngineJson, setActiveEngineJson] = useState(null); // default: NULL
    // always refresh 'activeEngineJson' when 'activeEngineId' changes
    useEffect(() => {
        
        // Edge case: Never fetch() if 'activeEngineId' is NULL or UNDEFINED
        if (activeEngineId == null) return;

        // Define async fetch() function
        async function fetchActiveEngineJson() {
            
            // `GET /engines/{activeEngineId}`
            try {
                
                // Edge case: Never fetch() if <Clerk JWT> DNE
                const token = await getToken();
                if (!token) { return };
                
                const engine_response = await fetch(`${API_BASE_URL}/api/v1/engines/${encodeURIComponent(activeEngineId)}`);
                
                // error handling
                if (!engine_response.ok) { throw new Error(`SKYBASE (client): Cannot fetch activeEngineJson in PES.js`)};
                
                // extract Engine JSON
                const engine_json = await engine_response.json();

                // update JSON (triggers re-render)
                setActiveEngineJson(engine_json);

            } catch (err) {
                console.error(err);
            }
        }

        // Immediately fetch() active Engine JSON
        fetchActiveEngineJson();

    }, [activeEngineId]);



    // ======================
    // AVAILABLE ENGINES JSON – a JSON[] containing all publically-available Skybase Engine data.
    // ======================
    // state var
    const [availableEnginesJson, setAvailableEnginesJson] = useState(() => {
        
        // retrieve localStorage cache, otherwise []
        const saved = localStorage.getItem("availableEnginesJson");
        return saved ? JSON.parse(saved) : [];
    });
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("availableEnginesJson", JSON.stringify(availableEnginesJson));
    }, [availableEnginesJson]);
    // onMount – immediately fetch() available Engine JSON
    useEffect(() => {
        fetchAvailableEnginesJson();
    }, []);
    // async fetch() function
    async function fetchAvailableEnginesJson() {
        
        while (true) {
            try {
            // GET /engines/
            const url = new URL(`${API_BASE_URL}/api/v1/engines`);
            const response = await fetch(url);
            const engines_json = await response.json();

            // update JSON (triggers re-render)
            setAvailableEnginesJson(engines_json);

            // SUCCESS → exit loop
            break;

            // error handling
            } catch (err) {
                
                // log error --> wait 3 seconds before retrying
                console.error("(AvailableEnginesJson) Fetch failed. Retrying in 3 seconds...", err);
                await sleep(3000);
            }
        }   
    }


    // =============
    // CATEGORY JSON – a UUID[] containing the Postgre UUIDs of all Skybase categories.
    // =============
    // state var
    const [categoryJson, setCategoryJson] = useState(() => {
        
        // retrieve localStorage cache, otherwise [] 
        const saved = localStorage.getItem("categoryJson");
        return saved ? JSON.parse(saved) : [];
    });
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("categoryJson", JSON.stringify(categoryJson));
    }, [categoryJson]);
    // onMount – immediately fetch() categoryJSON
    useEffect(() => {
        fetchCategoryJson();
    }, []);
    // async fetch() function
    async function fetchCategoryJson() {
        try {
            // `GET /categories/`
            const cat_response = await fetch(`${API_BASE_URL}/api/v1/categories`);
            if (!cat_response.ok) { throw new Error(`SKYBASE (client): HTTP ${cat_response.status} – Unable to fetch ALL Categories.`)};
            const cat_json = await cat_response.json();

            // update JSON (triggers re-render)
            setCategoryJson(cat_json);
        } catch (err) {
            console.error(err);
        }
    }


    // =============
    // ORGANIZATIONS – a JSON[] containing all info about Skybase organizations.
    // =============
    // state var
    const [organizations, setOrganizations] = useState(() => {
        
        // retrieve localStorage cache, otherwise []
        const saved = localStorage.getItem("organizations");
        return saved ? JSON.parse(saved) : [];
    });
    // localStorage caching
    useEffect(() => {
        localStorage.setItem("organizations", JSON.stringify(organizations));
    }, [organizations]);
    // async fetch() function
    useEffect(() => {
        async function fetchOrganizationsJson() {
            try {
                // `GET /organizations/`
                const org_response = await fetch(`${API_BASE_URL}/api/v1/organizations`);
                if (!org_response.ok) { throw new Error(`SKYBASE (client): HTTP ${org_response.status} – Unable to fetch ALL organizations.`)};
                
                // extract JSON
                const org_json = await org_response.json();

                // update JSON (triggers re-render)
                setOrganizations(org_json)

            } catch (err) {
                console.error(err);
            }
        }

        fetchOrganizationsJson();
    }, []);
    
    
    // ==============
    // LOADING SCREEN
    // ==============
    const [isLoading, setIsLoading] = useState(false);
    function enableLoadingScreen() { setIsLoading(true); }
    function disableLoadingScreen() { setIsLoading(false); }

    
    // =========
    // BOOKMARKS – a JSON[] containing info. about a Clerk CurrentUser's bookmarks in the currently-active Skybase Engine.
    // =========
    // state vars
    const [activeEngineBookmarkJson, setActiveEngineBookmarkJson] = useState(() => {
        
        // retrieve localStorage cache, otherwise []
        const saved = localStorage.getItem("activeEngineBookmarkJson");
        return saved ? JSON.parse(saved) : [];
    })
    // localStorage caching
    useEffect(() => {

        localStorage.setItem("activeEngineBookmarkJson", JSON.stringify(activeEngineBookmarkJson));
    }, [activeEngineBookmarkJson])
    // onMount --> immediately fetch() the Bookmark JSON in the active Engine.
    useEffect(() => {
        
        // Edge case: Never fetch() if UNDEFINED vars.
        if (!activeUserJson || !activeEngineId) { return }

        loadActiveEngineBookmarkJson();

    }, [activeUserJson, activeEngineId])
    // createBookmark(String) -> Creates a Bookmark for the Axum CurrentUser.
    async function createBookmark(itemId) {        
        
        // Edge case: Never fetch() with UNDEFINED inputs
        if (!isSignedIn || !itemId) { return };

        // Edge case: Never fetch() a subscribed-route without a <Clerk JWT>
        const token = await getToken();
        if (!token) { return };

        // create url
        const url = new URL(`${API_BASE_URL}/api/v1/bookmarks`);

        // create payload
        const json_payload = {
            user_id: "00000000-0000-0000-0000-000000000000",      // the user_id will be set by the backend using the auth_middleware CurrentUser Extractor.
            item_id: itemId,
        }

        // `POST /bookmarks/`
        try {
            const res = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(json_payload),
            });

            if (!res.ok) { throw new Error(`SKYBASE (Client): HTTP ${res.status} Error – Unable to create Bookmark`); }
            const data = await res.json();

            // ⚠️ Developer debugging
            // console.log(`SKYBASE (client): Created Bookmark for Item ID: ${itemId}`);

            // re-fresh 'activeEngineBookmarkJson'
            loadActiveEngineBookmarkJson();

        } catch (err) {
            console.error("Error:", err);
        }
    }
    // removeBookmark(String) --> Deletes a particular CurrentUser's Bookmark.
    async function removeBookmark(bookmarkId) {
        // error-check
        if (!bookmarkId) { return };

        // create url
        const token = await getToken();
        const url = new URL(`${API_BASE_URL}/api/v1/bookmarks/${bookmarkId}`);

        // `DELETE /bookmarks/{id}`
        try {
            const res = await fetch(url, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            });

            if (!res.ok) { throw new Error(`SKYBASE (Client): HTTP ${res.status} Error – Unable to DELETE Bookmark (ID: ${bookmarkId})`);}

            // ⚠️ Developer debugging
            // console.log(`SKYBASE (client): Deleted Bookmark ID: ${bookmarkId}`);

            // re-fresh 'activeEngineBookmarkJson'
            loadActiveEngineBookmarkJson();

        } catch (err) {
            console.error("Error:", err);
        }
    }
    // loadActiveEngineBookmarkJson() --> async. fn that refreshes the 'activeEngineBookmarkJson' with the most recent Postgres data.
    async function loadActiveEngineBookmarkJson() {
        try {

            // Exit condition: Never fetch() a subscribed-route if CurrentUser !isSubscribed.
            if (!(await checkIsSubscribed())) { return; }

            // Exit condition: Never fetch() a subscribed-route if <Clerk JWT> non-existent.
            const token = await getToken();
            if (!token) { return };

            // `GET /bookmarks?user_id={}&engine_id={}`
            const url = new URL(`${API_BASE_URL}/api/v1/bookmarks`);
            url.searchParams.set("engine_id", activeEngineId);

            const bookmark_response = await fetch(url, 
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const bookmark_json = await bookmark_response.json();

            // ⚠️ Developer debugging
            // console.log(`SKYBASE (client): Active Engine Bookmark JSON`);
            // console.log(bookmark_json);

            // update JSON (triggers re-render)
            setActiveEngineBookmarkJson(bookmark_json);

        } catch (err) {
            console.error(err);
        }
    }


    // =================
    // DIRECTORY NODE ID
    // ⚠️ TO–DO: Add localStorage caching (requires persisting the FolderNodeStack and syncing everything up across DataContext.js and Directory.js)
    // =================
    const DEFAULT_NODE_ID = "00000000-0000-0000-0000-000000000000"; // The Engine Root-Node
    // state var
    const [directoryNodeId, setDirectoryNodeId] = useState(DEFAULT_NODE_ID); // default: The Engine Root-Node
    // changeDirectoryNodeId(String) --> setter for directoryNodeId
    function changeDirectoryNodeId(newId) {
        setDirectoryNodeId(newId);
    }
    // onChange --> always reset directoryNodeId when 'activeEngineId' changes
    useEffect(() => {

        // Edge case: Switching activeEngineId w/o ever traversing OFF the default node
        if (directoryNodeId === DEFAULT_NODE_ID) {
            // manually trigger a re-fetch()
            fetchDirectoryChildrenId();

        } else {
            
            // Otherwise, just reset the directoryNodeId (which automatically triggers a re-fetch() via useEffect)
            setDirectoryNodeId(DEFAULT_NODE_ID);
        }
    }, [activeEngineId]);


    // ========================
    // DIRECTORY CHILDREN ID [] – a UUID[] filled with the UUIDs of all children of the current 'directoryNodeId'.
    // ========================
    // state var
    const [directoryChildrenId, setDirectoryChildrenId] = useState(null); // default: NULL
    // onMount --> fetch() all children
    useEffect(() => {
        fetchDirectoryChildrenId();
    }, []);
    // onChange of 'directoryNodeId' --> fetch() all new children
    useEffect(() => {
        fetchDirectoryChildrenId();
    }, [directoryNodeId]);
    // fetchDirectoryChildrenId() --> async fetch() to refresh the 'directoryChildrenId' of type UUID[]
    async function fetchDirectoryChildrenId() {

        // Exit condition: Clerk User isLoading.
        if (isLoading) { return; };

        // Exit condition: activeEngineId === NULL
        if (!activeEngineId) { return; };

        while (true) {
            try {

                // Exit condition: Never fetch() a subscribed-route if CurrentUser !isSubscribed.
                if (!(await checkIsSubscribed())) { return; }

                // Exit condition: Never fetch() a subscribed-route if <Clerk JWT> non-existent.
                const token = await getToken();
                if (!token) { return };

                // ⚠️ Developer debugging
                // console.log(`SKYBASE (client): Curr. Directory Node ID`);
                // console.log(directoryNodeId);
            
                // GET /folders/{id}/children
                const url = new URL(`${API_BASE_URL}/api/v1/folders/${encodeURIComponent(directoryNodeId)}/children`);
                url.searchParams.set("engine_id", activeEngineId);

                const response = await fetch(url,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                
                const children_id = await response.json();

                // debugging
                // console.log("DIR CHILDREN IDs:");
                // console.log(children_id);

                // trigger re-render
                setDirectoryChildrenId(children_id);

                // SUCCESS → exit loop
                break;

            } catch (err) {
                console.error("(directoryChildrenId) Fetch failed. Retrying in 3 seconds...", err);

                // wait 3 seconds before retrying
                await sleep(3000);
            }
        }   
    }


    // =================
    // FOLDER NODE STACK – FILO Stack to track a CurrentUser's location within the /directory page Folder Tree.
    // =================
    // state var
    const [folderNodeStack, setFolderNodeStack] = useState([]); // default: Empty stack
    // onChange of 'activeEngineId' --> reset the folderNodeStack
    useEffect(() => {
        setFolderNodeStack([]);
    }, [activeEngineId])
    // pushFolderNodeStack(id: String) --> appends a Folder UUID to the END of the FILO Stack.
    const pushFolderNodeStack = (id) => {
        setFolderNodeStack(prev => [...prev, id]);
    }
    // popFolderNodeStack() --> dynamically traverses to the parent Node Id.
    const popFolderNodeStack = () => {
        
        // Edge case: If stack is empty, changeDirectoryNodeId(DEFAULT_NODE_ID)
        if (folderNodeStack.length <= 1) {
            // reset FILO stack to default node
            changeDirectoryNodeId("00000000-0000-0000-0000-000000000000");

            // reset stack
            setFolderNodeStack([]);
            
            // exit immediately
            return;
        }

        // identify parent and curr nodes
        const parentNodeId = folderNodeStack.at(-2);
        const currNodeId = folderNodeStack.at(-1);

        // remove curr node from stack
        setFolderNodeStack(prev => prev.filter(nodeId => nodeId !== currNodeId));

        // finally, traverse to the parent node
        changeDirectoryNodeId(parentNodeId);    
    }
    // ⚠️ Developer debugging
    // useEffect(() => {
    //     console.log(`SKYBASE (client): Folder Node Stack`);
    //     console.log(folderNodeStack);
    // }, [folderNodeStack])


    // ==============
    // PINNED ENGINES – a JSON[] containing the JSON of every pinned Engine of a CurrentUser.
    // ==============
    // state var
    const [pinnedEngineJson, setPinnedEngineJson] = useState(() => {
        
        // retrieve localStorage cache, otherwise []
        const saved = localStorage.getItem("pinnedEngineJson");
        return saved ? JSON.parse(saved) : [];
    })
    // localStorage caching
    useEffect(() => {

        localStorage.setItem("pinnedEngineJson", JSON.stringify(pinnedEngineJson));
    }, [pinnedEngineJson]);
    // fetchPinnedEngineJson() --> async. fetch() of the JSON of every pinned Engine of the CurrentUser.
    async function fetchPinnedEngineJson() {

        // Exit condition: Never fetch() a subscribed-route if CurrentUser !isSubscribed.
        if (!(await checkIsSubscribed())) { return; }

        // Exit condition: Never fetch() a subscribed-route if <Clerk JWT> non-existent.
        const token = await getToken();
        if (!token) { return };

        // GET /users/pinned-engines
        const url = new URL(`${API_BASE_URL}/api/v1/users/pinned-engines`);
        const response = await fetch(url,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );
        const pinned_engine_json = await response.json();
        
        // update JSON state var (triggers re-render)
        setPinnedEngineJson(pinned_engine_json);
    }
    // onMount --> immediately fetch() pinned Engine JSON.
    useEffect(() => {
        fetchPinnedEngineJson();
    }, []);
    // onChange ('activeUserJson') --> immediately fetch() pinned Engine JSON.
    useEffect(() => {
        fetchPinnedEngineJson();
    }, [activeUserJson]);
    // pinEngine(engine_id: String) --> async. fn that pins an Engine.
    const pinEngine = async (engine_id) => {

        // Edge case: Never fetch() with UNDEFINED inputs
        if (!engine_id) { return };

        // Edge case: Never fetch() a subscribed-route without a <Clerk JWT>
        const token = await getToken();
        if (!token) { return };
        
        // POST `users/pinned-engines/{engine_id}`
        const url = new URL(
            `${API_BASE_URL}/api/v1/users/pinned-engines/${encodeURIComponent(engine_id)}`
        );

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to pin engine: ${response.status}`);
        }

        // refresh the JSON
        fetchPinnedEngineJson();

        return response;
    }
    // unpinEngine(engine_id: String) --> async. fn unpins an Engine.
    const unpinEngine = async (engine_id) => {
     
        // Edge case: Never fetch() with UNDEFINED inputs
        if (!engine_id) { return };

        // Edge case: Never fetch() a subscribed-route without a <Clerk JWT>
        const token = await getToken();
        if (!token) { return };

        // DELETE `users/pinned-engines/{engine_id}`
        const url = new URL(
            `${API_BASE_URL}/api/v1/users/pinned-engines/${encodeURIComponent(engine_id)}`
        );

        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`Failed to unpin engine: ${response.status}`);
        }

        // refresh the JSON
        fetchPinnedEngineJson();

        return response;
    }

    // ⚠️ Developer debugging
    // useEffect(() => {
    //     console.log(`SKYBASE (cline) Pinned Engine JSON`);
    //     console.log(pinnedEngineJson);
    // }, [pinnedEngineJson]);


    // JSX Component – React Provider
    return(
        <DataContext.Provider
            value={{
                // ENGINES
                activeEngineJson,                   // JSON
                availableEnginesJson,               // JSON[]
                changeActiveEngineId,               // async fn(newEngineid: String)
                // ORGANIZATIONS
                organizations,                      // JSON[]
                // LOADING SCREEN
                isLoading,                          // Boolean
                enableLoadingScreen,                // fn()
                disableLoadingScreen,               // fn()
                // BOOKMARKS
                activeEngineBookmarkJson,           // JSON[]
                createBookmark,                     // async fn(itemId: String)
                removeBookmark,                     // async fn(bookmarkId: String)
                // FOLDER DIR NODE
                changeDirectoryNodeId,              // fn(newNodeId: String)
                directoryChildrenId,                // String[]
                folderNodeStack,                    // String[]
                popFolderNodeStack,                 // fn()
                pushFolderNodeStack,                // fn(id: String)
                // PINNED ENGINES
                pinnedEngineJson,                   // JSON[]
                fetchPinnedEngineJson,              // async fn()
                pinEngine,                          // async fn(engine_id: String)
                unpinEngine,                        // async fn(engine_id: String)
                // CATEGORIES
                categoryJson,                       // JSON[]
            }}
        >
            {children}
        </DataContext.Provider>
    )
}


// Create the Skybase 'useData' hook
export function useData() {
    return useContext(DataContext)
}